import React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { useDaumPostcodePopup } from 'react-daum-postcode';

import { Bold, Medium } from './Text';
import { FONT, BG } from '../../styles/color';

const TitleWrapper = styled.div`
	display: inline-block;
`;

const LineWrapper = styled.div`
	width: 100%;
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 35px;
`;

const FieldTitle = styled.div`
	display: flex;
	align-items: center;
	width: 130px;
	height: 50px;
`;

const FieldText = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	flex-grow: 1;
	height: 50px;
	box-sizing: border-box;
`;

export const FieldInput = styled.input`
	flex-grow: 1;
	height: 50px;
	border: 1px solid ${ FONT.deep01 };
	box-sizing: border-box;
	border-radius: 8px;
	font: normal 400 18px Pretendard Variable;
	color: ${ FONT.default };
	padding: 0 10px;

	&::placeholder {
		color: ${ FONT.deep01 };
	}

	&:focus {
		outline: none;
	}

	&::-webkit-outer-spin-button,
  	&::-webkit-inner-spin-button {
    	-webkit-appearance: none;
    	margin: 0;
  	}

  	&[type=number] {
    	-moz-appearance: textfield;
  	}
`;

export const InputWrapper = styled.div`
	height: 50px;
	flex-grow: 1;
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	gap: 10px;
`;

const Button = styled.button`
	width: ${ props => props.width || '130px' };
	background: ${ props => props.color === 'dark' ?  FONT.default : BG.light01 };
	border: ${ props => props.color === 'dark' ?  'none' : `1px solid ${ FONT.deep01 }` };
	box-sizing: border-box;
	border-radius: 8px;
	cursor: pointer;
`;

const AddressWrapper = styled.div`
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 10px;
`;

const SelectWrapper = styled.select`
  	-moz-appearance: none;
  	-webkit-appearance: none;
  	appearance: none;

	width: 200px;
	height: 50px;
	flex-grow: 1;
	box-sizing: border-box;
	border: 1px solid ${ FONT.deep01 };
	border-radius: 8px;
	background-color: ${ BG.light01 };
	color: ${ FONT.default };
	font: normal 400 18px Pretendard Variable;
	text-align: center;
`;

export function FormTitle(props) {
	return (
		<TitleWrapper style={ props.style }>
			<Bold size="26px">{props.children}</Bold>
		</TitleWrapper>
	);
}

export function FieldContainer(props) {
	return (
		<InputWrapper style={ props.style }>
			{ props.children }
		</InputWrapper>
	);
}

export function TextField(props) {
	return (
		<FieldInput type="text" placeholder={ props.$placeholder }
			name={ props.name } value={ props.value }
			onChange={ props.onHandleChange }
		/>
	);
}

export function DropDownField(props) {
	return(
		<SelectWrapper
			name={ props.name } value={ props.value } 
			onChange={ props.onChange }
		>
			{
				Object.entries(props.list).map(([key, value]) => (
					<option key={key} value={key}>{value}</option>
				)) 
			}
		</SelectWrapper>
	);
}

export function FormButton(props) {
	return (
		<Button color={props.color} width={props.width} onClick={props.onClick}>
			<Medium size="18px" color={ props.color === 'dark' ?  FONT.light01 : BG.default }>{props.value}</Medium>
		</Button>
	);
}

export function AddressField(props) {
	const open = useDaumPostcodePopup();

	const handleComplete = (data) => {
		let fullAddress = data.address;
		let extraAddress = '';

		if (data.addressType === 'R') {
			if (data.bname !== '') {
				extraAddress += data.bname;
			}
			if (data.buildingName !== '') {
				extraAddress += extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName;
			}
			fullAddress += extraAddress !== '' ? ` (${extraAddress})` : '';
		}

		props.onAddressComplete({ postalCode: data.zonecode, roadAddress: fullAddress });
  	};

	const handleClick = () => {
		open({ onComplete: handleComplete });
  	};



	return (
		<LineWrapper>
			<FieldTitle>
				<Medium size="18px">{ props.title ? props.title : '주소'}</Medium>
			</FieldTitle>
			<AddressWrapper>
				<InputWrapper>
					<FieldInput type="text" style={{ cursor: 'pointer' }} readOnly
						name="postalCode" value={ props.value && props.value.postalCode || '' }
						onChange={ props.onHandleChange }
					/>
					<FormButton onClick={ handleClick } color="dark" value="주소찾기">
					</FormButton>
				</InputWrapper>
				<FieldInput type="text" style={{ cursor: 'pointer' }} readOnly
					name="roadAddress" value={ props.value && props.value.roadAddress || '' }
					onChange={ props.onHandleChange }
				/>
				<FieldInput type="text" style={{ cursor: 'pointer' }} 
					name="detailAddress" value={ props.value && props.value.detailAddress || '' }
					onChange={ props.onHandleChange }
				/>
			</AddressWrapper>
		</LineWrapper>
	);
}

export function Field(props) {
	if(props.type === 'text') {
		return (
			<LineWrapper>
				<FieldTitle><Medium size="18px">{props.title}</Medium></FieldTitle>
				<FieldInput type={props.type} placeholder={ props.$placeholder }
					name={props.name} value={props.value}
					onChange={props.onHandleChange}
				/>
			</LineWrapper>
		);
	}

	if(props.type === 'tel') {
		return (
			<LineWrapper>
				<FieldTitle><Medium size="18px">{props.title}</Medium></FieldTitle>
				<FieldInput type="number" placeholder={ props.$placeholder }
					name={props.name} value={props.value}
					onChange={props.onHandleChange}
				/>
			</LineWrapper>
		);
	}

	if(props.type === 'password') {
		const placeholder = props.$placeholder || '영문, 숫자 조합 5자 이상';
		return (
			<LineWrapper>
				<FieldTitle><Medium size="18px">{props.title}</Medium></FieldTitle>
				<FieldInput type="password" placeholder={ placeholder }
					name={props.name} value={props.value}
					onChange={props.onHandleChange}
				/>
			</LineWrapper>
		);
	}

	if(props.type === 'button') {
		return (
			<LineWrapper>
				<FieldTitle><Medium size="18px">{props.title}</Medium></FieldTitle>
				<InputWrapper>
				{
					props.editable ? (
						<FieldInput type="text" placeholder={ props.$placeholder }
							name={props.name} value={props.value}
							onChange={props.onHandleChange}
						/>
					) : (
						<FieldText><Medium size="18px">{ props.value }</Medium></FieldText>
					)
				}
					<FormButton onClick={ props.onClick }
						color="dark"
						value={ props.buttonText }
					/>
				</InputWrapper>
			</LineWrapper>
		);
	}

	if(props.type === 'dropDown') {
		return (
			<LineWrapper>
				<FieldTitle><Medium size="18px">{props.title}</Medium></FieldTitle>
				<DropDownField 
					list={ props.list }
					name={ props.name } value={ props.value }
					onChange={ props.onHandleChange }
				/>
			</LineWrapper>
		)
	}

	return (
		<LineWrapper>
			<FieldTitle><Medium size="18px">{props.title}</Medium></FieldTitle>
			<AddressWrapper>
				{
					Array.isArray(props.value) ? (
						props.value.map((item, index) => (
							<FieldText key={ index }><Medium size="18px">{item}</Medium></FieldText>
						))
					) : (
						<FieldText><Medium size="18px">{props.value}</Medium></FieldText>
					)
				}
			</AddressWrapper>
		</LineWrapper>
	);
}